import React, { Fragment, useState, useEffect, useCallback } from 'react';
import { GetList, AddItem, UpdateItem } from '../../REST/crud';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import { useForm, Controller } from "react-hook-form";
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import NativeSelect from '@mui/material/NativeSelect';
import FormHelperText from '@mui/material/FormHelperText';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import PageShell from '../../themes/components/PageShell';
import PageHeader from '../../themes/components/PageHeader';
import PageContent from '../../themes/components/PageContent';
import Action from '../../themes/components/Action';
import ResponsiveDataGrid from '../../themes/components/ResponsiveDataGrid';
import WorkflowDialogShell from '../../themes/components/WorkflowDialogShell';

export default function PropertyAttributes() {
    const [mode, setMode] = useState(null)
    const navigate = useNavigate()
    const [rowData, setRowData] = useState(null)

    const columns = [
        { field: 'name', headerName: 'Name', flex: 0.3 },
        { field: 'description', headerName: 'Description', flex: 0.3 },
        { field: 'attributeType', headerName: 'Type', flex: 0.2, minVisible: 'md' },
        { 
            field: 'allowFiltering', 
            headerName: 'Filtering', 
            flex: 0.1,
            minVisible: 'md',
            valueGetter: (params) => params.row.allowFiltering ? 'Yes' : 'No'
        },
        { 
            field: 'allowSorting', 
            headerName: 'Sorting', 
            flex: 0.1,
            minVisible: 'md',
            valueGetter: (params) => params.row.allowSorting ? 'Yes' : 'No'
        },
        {
            field: 'actions', 
            headerName: 'Actions', 
            flex: 0.1, 
            renderCell: (params) => {
                return <IconButton onClick={(e) => { e.stopPropagation(); handleEdit(params.row) }}><EditIcon /></IconButton>
            }
        }
    ];

    const fetchPropertyAttributes = useCallback(async (page, limit, filters, sort) => {
        const params = {
            page,
            limit
        }
        
        if (Object.keys(filters).length > 0) {
            params.filters = JSON.stringify(filters)
        }
        
        if (sort) {
            params.sort = JSON.stringify(sort)
        }
        
        const result = await GetList('PropertyAttribute', params);

        if (result.data?.data) {
            return {
                data: result.data.data,
                pagination: result.data.pagination
            };
        }

        if (Array.isArray(result.data)) {
            return {
                data: result.data,
                pagination: { total: result.data.length, page: 1, totalPages: 1 }
            };
        }

        return { data: [], pagination: { total: 0, page: 1, totalPages: 0 } };
    }, []);

    const handleMode = (mode) => { setMode(mode) }

    const handleData = (data) => {
        if (rowData && rowData._id) {
            UpdateItem('PropertyAttribute', rowData._id, data).then(() => {
                setMode(null)
                setRowData(null)
            })
        } else {
            AddItem('PropertyAttribute', data).then(() => {
                setMode(null)
                setRowData(null)
            })
        }
    }

    const handleEdit = (value) => {
        setRowData(value)
        handleMode(true)
    }

    const handleAdd = () => {
        setRowData(null)
        handleMode(true)
    }

    return <Fragment>
        <PageShell>
            <PageHeader
                title="Property Attributes"
                back
                onBack={() => navigate(-1)}
                actions={
                    <Action
                        primary
                        icon={<AddIcon />}
                        label="Add Property Attribute"
                        onClick={handleAdd}
                    />
                }
            />

            {mode !== null && <FormDialog open={mode !== null} handleClose={() => { handleMode(null); setRowData(null) }} handleSave={handleData} attrData={rowData} />}

            <PageContent fill>
                <ResponsiveDataGrid
                    columns={columns}
                    fetchData={fetchPropertyAttributes}
                    initialPageSize={10}
                    pageSizeOptions={[10, 25, 50, 100]}
                    getRowId={(i) => i?._id}
                />
            </PageContent>
        </PageShell>
    </Fragment>
}


function FormDialog({ open = false, data, handleClose, handleSave, attrData }) {
    const schema = yup.object({
        name: yup.string().trim().required(),
        description: yup.string().trim().required(),
        attributeType: yup.string().trim().required()
    }).required();

    const { control, handleSubmit, formState: { errors }, clearErrors, reset } = useForm({
        defaultValues: {
            name: '',
            description: '',
            attributeType: '',
            allowFiltering: false,
            allowSorting: false
        },
        resolver: yupResolver(schema)
    });

    useEffect(() => {
        if (attrData) {
            const { name, description, attributeType, allowFiltering, allowSorting } = attrData
            reset({ name, description, attributeType, allowFiltering, allowSorting });
        }
    }, [attrData, reset]);

    useEffect(() => {
        clearErrors()
        reset()
    }, [open, clearErrors, reset])

    const onSubmit = handleSave

    const isError = (val) => val !== undefined && val !== null && typeof val === 'object'

    const getOptions = (opts = ['Text', 'Numeric', 'Date']) => {
        return [<option key='temp'>Select One</option>].concat(opts.map((e, idx) => {
            return <option key={idx} value={e}>{e}</option>
        }))
    }


    return <WorkflowDialogShell
        open={open}
        onClose={handleClose}
        onSave={handleSubmit(onSubmit)}
        title="Property Attribute"
    >
        <>
            <Controller name="name" control={control} render={({ field }) => <TextField margin='dense' error={isError(errors.name)} helperText={errors.name && errors.name.message} fullWidth label='Name' {...field} />} />
            <Controller name="description" control={control} render={({ field }) => <TextField margin='dense' error={isError(errors.description)} helperText={errors.description && errors.description.message} fullWidth label='Description' {...field} />} />
            <Controller name="attributeType" control={control} render={({ field }) => <FormControl error={isError(errors.attributeType)} variant='standard' required margin='dense' fullWidth><InputLabel>Attribute Type</InputLabel><NativeSelect {...field} >{getOptions()}</NativeSelect>{errors.attributeType && <FormHelperText>{errors.attributeType.message}</FormHelperText>}</FormControl>} />
            <Controller name="allowFiltering" control={control} render={({ field }) => <FormControlLabel {...field} control={<Checkbox checked={field.value} />} label="Allow Filtering" />} />
            <Controller name="allowSorting" control={control} render={({ field }) => <FormControlLabel {...field} control={<Checkbox checked={field.value} />} label="Allow Sorting" />} />
        </>
    </WorkflowDialogShell>
}
